import React, { useState } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Check, Clock } from 'lucide-react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';

/**
 * Inline NI approval control for manager/admin users.
 * Shows checkbox for unapproved NI entries (manager/admin),
 * green check for approved, amber clock for pending (other roles).
 *
 * Follows the same inline pattern as VerificationControls.js.
 */
const NIApprovalControl = ({ commitment, onApprove, currentUser }) => {
  const [loading, setLoading] = useState(false);

  // Only render for not_interested commitments
  if (commitment.commitment_status !== 'not_interested') {
    return null;
  }

  const handleApprove = async () => {
    setLoading(true);
    try {
      await axiosInstance.put(`/commitments/${commitment.id}/approve-ni`);
      toast.success('NI approval confirmed');
      if (onApprove) onApprove(commitment.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve NI');
    } finally {
      setLoading(false);
    }
  };

  // Already approved
  if (commitment.ni_approved) {
    return (
      <div className="flex items-center gap-1.5 text-xs text-green-700" title={`Approved by ${commitment.ni_approved_by || 'Manager'}`}>
        <Check className="w-3.5 h-3.5 text-green-600" />
        <span>Approved</span>
      </div>
    );
  }

  // Manager or Admin can approve
  if (currentUser?.role === 'manager' || currentUser?.role === 'admin') {
    return (
      <div className="flex items-center gap-1.5">
        <Checkbox
          checked={false}
          onCheckedChange={handleApprove}
          disabled={loading}
          title="Approve Not Interested"
        />
        <label className="text-xs text-gray-600 cursor-pointer">
          {loading ? 'Approving...' : 'Approve NI'}
        </label>
      </div>
    );
  }

  // Other roles see pending status
  return (
    <div className="flex items-center gap-1.5 text-xs text-amber-600" title="Pending manager approval">
      <Clock className="w-3.5 h-3.5 text-amber-500" />
      <span>Pending Approval</span>
    </div>
  );
};

export default NIApprovalControl;
